/**
 * Client side of sync (SPEC §12): optimistic local transactions over a server-ordered log.
 *
 * The local graph is always "server state up to `cursor`" plus the pending local transactions on
 * top. When server ops arrive:
 * - our own oldest pending transaction coming back confirms it (it becomes base, no work);
 * - anything else is rebased under the pending ones: revert pending (exact inverses), apply the
 *   server ops, re-apply pending. A pending transaction that no longer applies is dropped; the
 *   server will reject it for the same reason, because both run the same deterministic core.
 */
import { applyWithConflicts, type Resolution } from './conflicts.ts'
import { OpError, type Graph } from './graph.ts'
import type { NodeId } from './ids.ts'
import type { Node } from './model.ts'
import type { OpBody } from './ops.ts'
import type { PushResult, ServerOp, WireTxn } from './protocol.ts'
import type { Rand } from './rng.ts'

interface Pending {
  readonly txn: WireTxn
  /** Inverse ops of the effective application (for undo). */
  inverse: OpBody[]
  /** Records before this transaction was applied locally (for exact rollback during a rebase). */
  prior: Map<NodeId, Node | undefined>
}

export class ResyncNeeded extends Error {
  constructor(reason: string) {
    super(reason)
    this.name = 'ResyncNeeded'
  }
}

export interface SyncOptions {
  readonly newId: () => string
  readonly now: () => number
  /** Randomness for keys chosen by conflict rules when replaying pending transactions. */
  readonly rand: Rand
  /** Continue a client's sequence across reloads. */
  readonly clientSeq?: number
  /** Called when a pending transaction's inverse changes after a rebase (keeps undo exact). */
  readonly onReinverse?: (txnId: string, inverse: OpBody[]) => void
  /** Called when a pending transaction is dropped (no longer applies). */
  readonly onDrop?: (txnId: string, reason: string) => void
}

export class SyncEngine {
  readonly g: Graph
  readonly clientId: string
  /** Highest server seq reflected in the base state. */
  cursor = 0
  private pending: Pending[] = []
  private seq: number
  private readonly opts: SyncOptions

  constructor(g: Graph, clientId: string, opts: SyncOptions) {
    this.g = g
    this.clientId = clientId
    this.opts = opts
    this.seq = opts.clientSeq ?? 0
  }

  get clientSeq(): number {
    return this.seq
  }

  get pendingCount(): number {
    return this.pending.length
  }

  /** Transactions waiting for the server, oldest first. Resending them is safe (idempotent). */
  outbox(): WireTxn[] {
    return this.pending.map((p) => p.txn)
  }

  /**
   * The server-confirmed version of a node: its record before the oldest pending transaction
   * touched it (undefined if a pending transaction created it). Persisting base versions, not the
   * optimistic graph, means a reload can replay the outbox exactly once.
   */
  baseOf(id: NodeId): Node | undefined {
    for (const p of this.pending) if (p.prior.has(id)) return p.prior.get(id)
    return this.g.get(id)
  }

  /** Every node as the server has it (base state), for a full mirror write. */
  baseNodes(): Node[] {
    const out: Node[] = []
    const seen = new Set<NodeId>()
    for (const p of this.pending) {
      for (const [id, rec] of p.prior) {
        if (seen.has(id)) continue
        seen.add(id)
        if (rec) out.push(rec)
      }
    }
    for (const n of this.g.nodes()) if (!seen.has(n.id)) out.push(n)
    return out
  }

  /** Ids touched by pending transactions (their base version differs from the graph). */
  touchedByPending(): Set<NodeId> {
    const ids = new Set<NodeId>()
    for (const p of this.pending) for (const id of p.prior.keys()) ids.add(id)
    return ids
  }

  /**
   * Apply ops as one local transaction and queue it. They run through the server's conflict rules,
   * so the local state is exactly what the server will make of them (a restored page whose title
   * is taken is renamed here too); what is queued is what was applied. Throws OpError if the rules
   * reject the transaction (nothing is applied then).
   */
  local(ops: readonly OpBody[], ts = this.opts.now()): { txnId: string; inverse: OpBody[] } {
    this.g.beginTrack()
    let res: Resolution
    let prior: Map<NodeId, Node | undefined>
    try {
      res = this.g.batch(() => applyWithConflicts(this.g, ops, ts, this.opts.rand))
    } finally {
      prior = this.g.endTrack()
    }
    if (!res.ok) throw new OpError(res.reason)
    const txn: WireTxn = {
      id: this.opts.newId(),
      ops: res.ops.map((body) => ({ id: this.opts.newId(), clientSeq: ++this.seq, ts, body })),
    }
    this.pending.push({ txn, inverse: res.inverses, prior })
    return { txnId: txn.id, inverse: res.inverses }
  }

  /** Replace the base with a snapshot, then re-apply pending transactions on top. */
  loadSnapshot(nodes: readonly Node[], seq: number): void {
    this.g.load(nodes)
    this.cursor = seq
    this.reapply(this.opts.now())
  }

  /** Restore pending transactions persisted before a reload (applied on top of the base). */
  restorePending(txns: readonly WireTxn[]): void {
    for (const txn of txns) {
      this.pending.push({ txn, inverse: [], prior: new Map() })
      for (const o of txn.ops) this.seq = Math.max(this.seq, o.clientSeq)
    }
    this.reapply(this.opts.now(), this.pending.length - txns.length)
  }

  /**
   * Server ops in seq order (from the WebSocket or a pull). Returns how many ops changed the
   * local state. Throws ResyncNeeded if a server op does not apply to the base (divergence).
   */
  receive(ops: readonly ServerOp[], ts = this.opts.now()): number {
    const fresh = ops.filter((o) => o.seq > this.cursor)
    if (fresh.length === 0) return 0
    let reverted = false
    let changed = 0
    for (const group of groupByTxn(fresh)) {
      const first = group[0]!
      // Our own transaction coming back: where is it in the pending queue?
      const k = first.clientId === this.clientId ? this.pending.findIndex((p) => p.txn.id === first.txnId) : -1
      if (k === 0 && !reverted && sameOps(this.pending[0]!.txn, group)) {
        // Fast path: the oldest pending transaction is confirmed as sent; its effect is already applied.
        this.pending.shift()
      } else {
        if (!reverted) {
          this.revertPending(ts)
          reverted = true
        }
        if (k > 0) {
          // The server applies a client's transactions in order, so the ones before it were rejected.
          for (const p of this.pending.splice(0, k)) this.opts.onDrop?.(p.txn.id, 'rejected by the server')
        }
        if (k >= 0) this.pending.shift()
        this.applyBase(
          group.map((o) => o.body),
          ts,
        )
        changed += group.length
      }
      this.cursor = group[group.length - 1]!.seq
    }
    if (reverted) this.reapply(ts)
    return changed
  }

  /** Push response: drop rejected transactions (rebasing the rest). Confirmations come via receive(). */
  acknowledge(result: PushResult, ts = this.opts.now()): void {
    if (result.rejected.length === 0) return
    const rejected = new Map(result.rejected.map((r) => [r.txnId, r.reason]))
    if (!this.pending.some((p) => rejected.has(p.txn.id))) return
    this.revertPending(ts)
    this.pending = this.pending.filter((p) => {
      const reason = rejected.get(p.txn.id)
      if (reason !== undefined) this.opts.onDrop?.(p.txn.id, `server: ${reason}`)
      return reason === undefined
    })
    this.reapply(ts)
  }

  private applyBase(bodies: OpBody[], ts: number): void {
    try {
      this.g.applyBatch(bodies, ts)
    } catch (e) {
      throw new ResyncNeeded(`server op does not apply locally: ${e instanceof Error ? e.message : String(e)}`)
    }
  }

  /** Undo the local effect of every pending transaction, newest first, exactly. */
  private revertPending(_ts: number): void {
    for (let i = this.pending.length - 1; i >= 0; i--) this.g.restoreRecords(this.pending[i]!.prior)
  }

  /**
   * Re-apply pending transactions from index `from` with the server's conflict rules, so the
   * local state predicts what the server will do with them. One that the rules reject is dropped
   * (the server will reject it too).
   */
  private reapply(ts: number, from = 0): void {
    const kept: Pending[] = this.pending.slice(0, from)
    for (const p of this.pending.slice(from)) {
      this.g.beginTrack()
      const res = this.g.batch(() =>
        applyWithConflicts(
          this.g,
          p.txn.ops.map((o) => o.body),
          ts,
          this.opts.rand,
        ),
      )
      const prior = this.g.endTrack()
      if (res.ok) {
        p.prior = prior
        p.inverse = res.inverses
        this.opts.onReinverse?.(p.txn.id, p.inverse)
        kept.push(p)
      } else {
        this.opts.onDrop?.(p.txn.id, res.reason)
      }
    }
    this.pending = kept
  }
}

/** True if the server logged exactly the ops we sent (no conflict rule rewrote the transaction). */
function sameOps(txn: WireTxn, group: readonly ServerOp[]): boolean {
  if (txn.ops.length !== group.length) return false
  for (let i = 0; i < group.length; i++) if (txn.ops[i]!.id !== group[i]!.id) return false
  return true
}

function groupByTxn(ops: readonly ServerOp[]): ServerOp[][] {
  const out: ServerOp[][] = []
  for (const o of ops) {
    const last = out[out.length - 1]
    if (last && last[0]!.txnId === o.txnId) last.push(o)
    else out.push([o])
  }
  return out
}

export type { NodeId }
